import React, { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import apiService, { SubscriptionStatus, Plan, PaymentRequest } from '../../services/api';
import AppLayout from '../shared/AppLayout';
import PlanSelector from './PlanSelector';
import PaymentModal from './PaymentModal';
import { useDesktopView } from '../../hooks/useDesktopView';
import { useAuth } from '../../contexts/AuthContext';

// Lazy load desktop component
const DesktopSubscriptionPage = lazy(() => import('./desktop/DesktopSubscriptionPage'));

// Loading fallback for desktop
const DesktopLoadingFallback: React.FC = () => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <motion.div
        className="w-10 h-10 border-3 border-gray-600 border-t-white rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Extended subscription status with balance eligibility
interface ExtendedSubscriptionStatus extends SubscriptionStatus {
  free_tier_eligible?: boolean;
  wallet_balance_usd?: number;
  balance_check_error?: string;
}

// Mobile Subscription Page Component
const MobileSubscriptionPage: React.FC = () => {
  const { pendingPlanId, clearPendingPlanId } = useAuth();
  const [subscription, setSubscription] = useState<ExtendedSubscriptionStatus | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expiredPaymentNotice, setExpiredPaymentNotice] = useState<string | null>(null);

  // Payment modal state
  const [payment, setPayment] = useState<PaymentRequest | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Track if we've processed the pending plan to avoid duplicate triggers
  const pendingPlanProcessed = useRef(false);
  const pendingPaymentChecked = useRef(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Check for existing pending payments and resume if found
  useEffect(() => {
    const checkPendingPayments = async () => {
      if (pendingPaymentChecked.current || loading || pendingPlanId) return;
      pendingPaymentChecked.current = true;

      try {
        const pendingPayment = await apiService.payments.getLatestPending();
        if (pendingPayment) {
          // Resume the pending payment
          setPayment(pendingPayment);
          setShowPaymentModal(true);
        }
      } catch {
        // Silently fail - this is a convenience feature
      }
    };

    checkPendingPayments();
  }, [loading, pendingPlanId]);

  // Auto-trigger payment modal for pending plan from website
  useEffect(() => {
    if (pendingPlanId && plans.length > 0 && !pendingPlanProcessed.current && !loading) {
      const targetPlan = plans.find(p => p.id === pendingPlanId);
      if (targetPlan) {
        pendingPlanProcessed.current = true;
        handleSelectPlan(targetPlan);
        clearPendingPlanId();
      } else {
        // Plan not found - clear the pending plan
        clearPendingPlanId();
      }
    }
  }, [pendingPlanId, plans, loading]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [subData, plansData] = await Promise.all([
        apiService.subscription.get(),
        apiService.plans.getAll(),
      ]);
      setSubscription(subData as ExtendedSubscriptionStatus);
      setPlans(plansData as Plan[]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (plan: Plan) => {
    setError(null);

    try {
      const paymentData = await apiService.payments.create(plan.id);
      setPayment(paymentData);
      setShowPaymentModal(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create payment');
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setPayment(null);
    fetchData(); // Refresh subscription status
  };

  const handleCloseModal = () => {
    // Check if payment expired while modal was open
    if (payment && new Date(payment.expires_at) < new Date()) {
      setExpiredPaymentNotice('Your previous payment request expired. Please start a new payment if you wish to subscribe.');
      // Auto-dismiss notice after 10 seconds
      setTimeout(() => setExpiredPaymentNotice(null), 10000);
    }
    setShowPaymentModal(false);
    setPayment(null);
  };

  // Determine current access status
  const getAccessStatus = () => {
    if (!subscription) return { hasAccess: false, reason: 'none' };

    if (subscription.tier === 'starter') {
      return { hasAccess: true, reason: 'starter' };
    }
    if (subscription.free_tier_eligible) {
      return { hasAccess: true, reason: 'free' };
    }
    return { hasAccess: subscription.is_active, reason: subscription.tier || 'none' };
  };

  const accessStatus = getAccessStatus();

  if (loading) {
    return (
      <AppLayout title="Pricing">
        <div className="min-h-screen bg-black text-white flex items-center justify-center">
          <motion.div
            className="w-10 h-10 border-3 border-gray-600 border-t-white rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Pricing">
      <div className="min-h-screen bg-black text-white p-4">
        {/* Access Status Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-xl ${
            accessStatus.hasAccess
              ? 'bg-gradient-to-r from-green-900/30 to-green-800/20 border border-green-500/30'
              : 'bg-gradient-to-r from-gray-900/50 to-gray-800/30 border border-gray-700/50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">
                {accessStatus.hasAccess ? 'Full Access Unlocked' : 'Choose Your Access'}
              </h2>
              <p className="text-sm text-gray-400">
                {accessStatus.reason === 'starter' && 'Lifetime Starter Access'}
                {accessStatus.reason === 'free' && `Free Tier - $${subscription?.wallet_balance_usd?.toFixed(0) || '500'}+ balance`}
                {!accessStatus.hasAccess && 'Select a tier below to get started'}
              </p>
            </div>
            <div className={`w-3 h-3 rounded-full ${accessStatus.hasAccess ? 'bg-green-500' : 'bg-gray-500'}`} />
          </div>
        </motion.div>

        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6 p-4 bg-red-900/30 border border-red-500/30 rounded-xl text-red-400"
          >
            {error}
          </motion.div>
        )}

        {/* Expired Payment Notice */}
        {expiredPaymentNotice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mb-6 p-4 bg-yellow-900/30 border border-yellow-500/30 rounded-xl text-yellow-400"
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p>{expiredPaymentNotice}</p>
              <button
                onClick={() => setExpiredPaymentNotice(null)}
                className="ml-auto text-yellow-400 hover:text-yellow-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}

        {/* Two-Tier Pricing Cards */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold mb-4 text-center">Simple Pricing</h3>
          <PlanSelector
            plans={plans}
            subscription={subscription}
            onSelectPlan={handleSelectPlan}
          />
        </div>

        {/* Access Details */}
        {accessStatus.hasAccess && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-gray-900/50 border border-gray-800 rounded-xl"
          >
            <h3 className="text-lg font-semibold mb-3">Your Access Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Access Type</span>
                <span className="text-green-400">
                  {accessStatus.reason === 'starter' ? 'Lifetime Starter' : 'Free Tier'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Max Capital</span>
                <span>${subscription?.max_capital_usd?.toLocaleString() || 'Unlimited'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Max Positions</span>
                <span>{subscription?.max_positions || 50}</span>
              </div>
              {accessStatus.reason === 'free' && subscription?.wallet_balance_usd && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Current Balance</span>
                  <span className="text-green-400">${subscription.wallet_balance_usd.toFixed(2)}</span>
                </div>
              )}
              {accessStatus.reason === 'starter' && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Expires</span>
                  <span className="text-green-400">Never</span>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && payment && (
          <PaymentModal
            payment={payment}
            onSuccess={handlePaymentSuccess}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </AppLayout>
  );
};

// Main SubscriptionPage component with responsive switching
const SubscriptionPage: React.FC = () => {
  const { isDesktop } = useDesktopView();

  if (isDesktop) {
    return (
      <Suspense fallback={<DesktopLoadingFallback />}>
        <DesktopSubscriptionPage />
      </Suspense>
    );
  }

  return <MobileSubscriptionPage />;
};

export default SubscriptionPage;
